import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { db, LocalRecord } from '../../utils/db';
import { calculateStreak } from '../../utils/habitUtils';
import { generateUUID } from '../../utils/security';
import { startOfDay, eachDayOfInterval, format, parseISO, subDays } from 'date-fns';

export type RecordStatus = 'pending' | 'completed' | 'skipped' | 'failed';

interface Record {
  id?: number;
  uuid: string;
  habitUuid: string;
  date: string;
  status: RecordStatus;
  note?: string;
}

export interface WeeklyHabitData {
  id: string; // uuid
  localId?: number; // IndexedDB id
  name: string;
  color: string;
  icon?: string;
  currentStreak: number;
  bestStreak: number;
  goalType: 'continuous' | 'weekly';
  weeklyGoal?: number;
  repeatDays?: {
    mon: boolean; tue: boolean; wed: boolean; thu: boolean;
    fri: boolean; sat: boolean; sun: boolean;
  };
  records: Array<{
    date: string;
    status: RecordStatus | null;
    note?: string;
  }>;
}

interface WeeklyViewData {
  startDate: string;
  endDate: string;
  habits: WeeklyHabitData[];
}

interface RecordsState {
  records: Record[];
  weeklyView: WeeklyViewData | null;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;
}

const initialState: RecordsState = {
  records: [],
  weeklyView: null,
  isLoading: false,
  isUpdating: false,
  error: null,
};

// Async thunks - IndexedDB を唯一の真実として使用
export const fetchWeeklyView = createAsyncThunk(
  'records/fetchWeeklyView',
  async (startDateStr?: string) => {
    const today = startOfDay(new Date());
    const endDate = today;
    const startDate = startDateStr ? parseISO(startDateStr) : subDays(today, 9);
    
    const dates = eachDayOfInterval({ start: startDate, end: endDate }).map(d => format(d, 'yyyy-MM-dd'));
    
    // 全ての非アーカイブ習慣を取得
    const habits = await db.habits
      .where('isArchived')
      .equals(0)
      .sortBy('displayOrder');
      
    const weeklyHabits: WeeklyHabitData[] = await Promise.all(habits.map(async (habit) => {
      // 期間内の記録を取得
      const records = await db.records
        .where('habitUuid')
        .equals(habit.uuid)
        .and(r => r.date >= dates[0] && r.date <= dates[dates.length - 1])
        .toArray();
        
      const recordMap = new Map(records.map(r => [r.date, r]));
      
      // ストリークを計算（唯一の真実である IndexedDB から算出）
      const stats = await calculateStreak(habit.uuid);
      
      // IndexedDB の習慣データ自体も最新のストリークで更新しておく（整合性のため）
      await db.habits.update(habit.id!, {
        currentStreak: stats.currentStreak,
        bestStreak: stats.bestStreak,
        totalCompleted: stats.totalCompleted,
        updatedAt: new Date(),
      });
      
      return {
        id: habit.uuid,
        localId: habit.id,
        name: habit.name,
        color: habit.color,
        icon: habit.icon,
        currentStreak: stats.currentStreak,
        bestStreak: stats.bestStreak,
        goalType: habit.goalType,
        weeklyGoal: habit.weeklyGoal,
        repeatDays: habit.repeatDays,
        records: dates.map(date => {
          const r = recordMap.get(date);
          return {
            date,
            status: r ? r.status : null,
            note: r ? r.note : undefined
          };
        })
      };
    }));

    return {
      startDate: dates[0],
      endDate: dates[dates.length - 1],
      habits: weeklyHabits
    };
  }
);

export const toggleRecord = createAsyncThunk(
  'records/toggleRecord',
  async ({ habitId, date }: { habitId: string; date: string }) => {
    // 既存の記録を確認
    const existingRecord = await db.records
      .where('[habitUuid+date]')
      .equals([habitId, date])
      .first();

    let newStatus: RecordStatus = 'completed';
    
    if (existingRecord) {
      // サイクル: completed -> skipped -> null (pending)
      if (existingRecord.status === 'completed') newStatus = 'skipped';
      else if (existingRecord.status === 'skipped') newStatus = 'pending';
    }

    if (!existingRecord) {
      const newRecord: LocalRecord = {
        uuid: generateUUID(),
        habitUuid: habitId,
        date,
        status: 'completed',
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      await db.records.add(newRecord);
      return { ...newRecord, status: 'completed' as RecordStatus };
    } else if (newStatus === 'pending') {
      await db.records.delete(existingRecord.id!);
      return { ...existingRecord, status: 'pending' as RecordStatus };
    } else {
      await db.records.update(existingRecord.id!, {
        status: newStatus,
        updatedAt: new Date(),
      });
      return { ...existingRecord, status: newStatus };
    }
  }
);

const recordsSlice = createSlice({
  name: 'records',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchWeeklyView.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchWeeklyView.fulfilled, (state, action) => {
      state.isLoading = false;
      state.weeklyView = action.payload;
    });
    builder.addCase(fetchWeeklyView.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || 'Failed to fetch records';
    });

    builder.addCase(toggleRecord.pending, (state) => {
      state.isUpdating = true;
    });
    builder.addCase(toggleRecord.fulfilled, (state) => {
      state.isUpdating = false;
    });
    builder.addCase(toggleRecord.rejected, (state, action) => {
      state.isUpdating = false;
      state.error = action.error.message || 'Failed to update record';
    });
  },
});

export const { clearError } = recordsSlice.actions;
export default recordsSlice.reducer;
